// Isometric orthographic camera rig: fixed azimuth (looking from +X +Z), tweenable target, view
// height (zoom) and elevation, with optional screen insets so a fit centres in the visible area.
import { OrthographicCamera, Vector3, Ray, Plane } from 'three';

export const ISO_ELEV = Math.atan(1 / Math.SQRT2); // true isometric, ≈ 35.26°
export const BUILDING_ELEV = 0.4; // ≈ 23°, flatter so stacked floors separate on screen
const AZIMUTH = Math.PI / 4;
const DISTANCE = 220;

const _v = new Vector3();
const _r = new Vector3();
const _u = new Vector3();
const _f = new Vector3();
const _ray = new Ray();
const _plane = new Plane();

export class CameraRig {
  constructor() {
    this.camera = new OrthographicCamera(-1, 1, 1, -1, 1, 800);
    this.target = new Vector3();
    this.viewH = 30;
    this.elev = ISO_ELEV;
    this.width = 1;
    this.height = 1;
    this.insets = { top: 0, right: 0, bottom: 0, left: 0 };
    this.apply();
  }

  setSize(w, h) {
    this.width = Math.max(1, w);
    this.height = Math.max(1, h);
    this.apply();
  }

  setInsets(insets = {}) {
    this.insets = {
      top: Math.max(0, Number(insets.top) || 0), right: Math.max(0, Number(insets.right) || 0),
      bottom: Math.max(0, Number(insets.bottom) || 0), left: Math.max(0, Number(insets.left) || 0),
    };
    this.apply();
  }

  /** Visible area (pixels) after insets, never smaller than a third of the canvas. */
  area() {
    const w = Math.max(this.width / 3, this.width - this.insets.left - this.insets.right);
    const h = Math.max(this.height / 3, this.height - this.insets.top - this.insets.bottom);
    return { w, h };
  }

  /** Camera basis for the current elevation: right, up, forward (unit vectors). */
  basis(elev = this.elev) {
    const ce = Math.cos(elev);
    _f.set(-Math.sin(AZIMUTH) * ce, -Math.sin(elev), -Math.cos(AZIMUTH) * ce).normalize();
    _r.set(Math.cos(AZIMUTH), 0, -Math.sin(AZIMUTH));
    _u.crossVectors(_r, _f).normalize();
    return { right: _r.clone(), up: _u.clone(), forward: _f.clone() };
  }

  /** World units per CSS pixel. */
  unitsPerPixel() {
    return this.viewH / this.height;
  }

  apply() {
    const cam = this.camera;
    const { forward } = this.basis();
    cam.position.copy(this.target).addScaledVector(forward, -DISTANCE);
    cam.up.set(0, 1, 0);
    cam.lookAt(this.target);
    const upp = this.unitsPerPixel();
    const hh = this.viewH / 2;
    const hw = hh * (this.width / this.height);
    // Offset so the target sits in the middle of the visible (inset) area.
    const dx = (this.insets.left - this.insets.right) / 2;
    const dy = (this.insets.top - this.insets.bottom) / 2;
    cam.left = -hw - dx * upp;
    cam.right = hw - dx * upp;
    cam.top = hh + dy * upp;
    cam.bottom = -hh + dy * upp;
    cam.near = 1;
    cam.far = DISTANCE * 3;
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld(true);
  }

  /**
   * Target and view height that frame `box` inside the visible area with a margin.
   * @param {import('three').Box3} box
   * `reserveRight` keeps that many pixels free on the right of the box (labels that hang there),
   * at most 45 % of the visible width.
   * @param {{elev?:number, margin?:number, reserveRight?:number}} [opts]
   * @returns {{target:Vector3, viewH:number}}
   */
  fit(box, { elev = this.elev, margin = 1.12, reserveRight = 0 } = {}) {
    const { right, up } = this.basis(elev);
    let minR = Infinity; let maxR = -Infinity; let minU = Infinity; let maxU = -Infinity;
    for (let i = 0; i < 8; i++) {
      _v.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z);
      const r = _v.dot(right);
      const u = _v.dot(up);
      minR = Math.min(minR, r); maxR = Math.max(maxR, r);
      minU = Math.min(minU, u); maxU = Math.max(maxU, u);
    }
    const area = this.area();
    const reserve = Math.min(Math.max(0, Number(reserveRight) || 0), area.w * 0.45);
    const w = area.w - reserve;
    const { h } = area;
    const needH = Math.max(maxU - minU, (maxR - minR) * (h / w)) * margin;
    const viewH = needH * (this.height / h);
    const center = box.getCenter(new Vector3());
    const cr = (minR + maxR) / 2 - center.dot(right);
    const cu = (minU + maxU) / 2 - center.dot(up);
    const target = center.clone().addScaledVector(right, cr).addScaledVector(up, cu);
    // The box slides left by half the reserved strip (the camera looks that much further right).
    if (reserve) target.addScaledVector(right, (reserve / 2) * (viewH / this.height));
    // Keep the target on the floor plane of the box (nicer panning and zoom anchoring).
    const { forward } = this.basis(elev);
    const t = (box.min.y - target.y) / forward.y;
    target.addScaledVector(forward, t);
    return { target, viewH };
  }

  /**
   * Camera target and view height for a pixel frame from framing.fitItems (screen x = ox + r·scale,
   * y = oy − u·scale in this rig's basis at `elev`). The target is kept on the plane y = planeY.
   * @param {{scale:number, ox:number, oy:number}} frame
   * @param {number} [elev]
   * @param {number} [planeY]
   * @returns {{target:Vector3, viewH:number}}
   */
  frameFromFit({ scale, ox, oy }, elev = this.elev, planeY = 0) {
    const { right, up, forward } = this.basis(elev);
    const s = Math.max(1e-6, scale);
    const { left, top } = this.insets;
    const cx = left + (this.width - left - this.insets.right) / 2;
    const cy = top + (this.height - top - this.insets.bottom) / 2;
    const target = right.multiplyScalar((cx - ox) / s).add(up.multiplyScalar((oy - cy) / s));
    target.addScaledVector(forward, (planeY - target.y) / forward.y);
    return { target, viewH: this.height / s };
  }

  /**
   * Intersection of the view ray under a pixel (CSS px, relative to the canvas) with the plane y = h.
   * @returns {Vector3|null}
   */
  groundAt(px, py, h = 0) {
    const cam = this.camera;
    const nx = (px / this.width) * 2 - 1;
    const ny = -(py / this.height) * 2 + 1;
    _v.set(nx, ny, -1).unproject(cam);
    _ray.origin.copy(_v);
    _ray.direction.set(0, 0, -1).transformDirection(cam.matrixWorld);
    _plane.set(_v.set(0, 1, 0), -h);
    return _ray.intersectPlane(_plane, new Vector3());
  }

  /** A world ray under a pixel. */
  rayAt(px, py, out) {
    const cam = this.camera;
    const nx = (px / this.width) * 2 - 1;
    const ny = -(py / this.height) * 2 + 1;
    out.origin.set(nx, ny, -1).unproject(cam);
    out.direction.set(0, 0, -1).transformDirection(cam.matrixWorld);
    return out;
  }

  /**
   * Projects a world point to CSS pixels relative to the canvas.
   * @returns {{x:number, y:number, inView:boolean}}
   */
  project(p) {
    _v.copy(p).project(this.camera);
    const x = (_v.x + 1) / 2 * this.width;
    const y = (1 - _v.y) / 2 * this.height;
    const m = 40;
    return { x, y, inView: x > -m && x < this.width + m && y > -m && y < this.height + m };
  }
}
